// /var/www/devit-dashboard/src/pages/Productos.js
import { useState, useEffect, useRef } from 'react';
import { productos as api } from '../services/api';

function formatPrecio(v) {
  if (v == null) return '—';
  return Number(v).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });
}

export default function Productos() {
  const [lista,      setLista]      = useState([]);
  const [total,      setTotal]      = useState(0);
  const [loading,    setLoading]    = useState(true);
  const [uploading,  setUploading]  = useState(false);
  const [busqueda,   setBusqueda]   = useState('');
  const [page,       setPage]       = useState(1);
  const [msg,        setMsg]        = useState(null);
  const fileRef = useRef();
  const LIMIT = 50;

  const cargar = (p = page, q = busqueda) => {
    setLoading(true);
    api.getAll({ busqueda: q, page: p, limit: LIMIT })
      .then(d => { setLista(d.productos || []); setTotal(d.total || 0); })
      .catch(() => setLista([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { cargar(1, ''); }, []); // eslint-disable-line

  const handleBuscar = (e) => {
    e.preventDefault();
    setPage(1);
    cargar(1, busqueda);
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setMsg(null);
    try {
      const res = await api.uploadFeed(file, 'espejo');
      setMsg({ tipo: 'ok', texto: `✅ ${res.importados} productos importados (feed total: ${res.total_feed})` });
      setPage(1);
      cargar(1, busqueda);
    } catch (err) {
      setMsg({ tipo: 'error', texto: `❌ Error al importar: ${err.message || 'Error desconocido'}` });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleBorrarTodos = async () => {
    if (!window.confirm(`¿Eliminar los ${total} productos del catálogo?`)) return;
    await api.borrarTodos();
    setMsg({ tipo: 'ok', texto: 'Catálogo vaciado.' });
    cargar(1, '');
  };

  const handleBorrar = async (id) => {
    if (!window.confirm('¿Eliminar este producto?')) return;
    await api.borrar(id);
    cargar(page, busqueda);
  };

  const totalPags = Math.ceil(total / LIMIT);

  return (
    <div style={{ padding: '24px', maxWidth: '1200px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: '20px' }}>Catálogo de Productos</h2>
        <span style={{ background: 'var(--bg3)', padding: '2px 10px', borderRadius: '12px', fontSize: '12px', color: 'var(--text2)' }}>
          {total} productos
        </span>
      </div>

      {/* Acciones */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <button
          onClick={() => fileRef.current.click()}
          disabled={uploading}
          style={{ padding: '8px 16px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}
        >
          {uploading ? 'Importando...' : '⬆ Importar XML (Google Shopping)'}
        </button>
        <input ref={fileRef} type="file" accept=".xml,.txt" style={{ display: 'none' }} onChange={handleUpload} />
        {total > 0 && (
          <button
            onClick={handleBorrarTodos}
            style={{ padding: '8px 16px', background: '#7f1d1d', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}
          >
            🗑 Vaciar catálogo
          </button>
        )}
      </div>

      {msg && (
        <div style={{
          marginBottom: '14px', padding: '10px 14px', borderRadius: '6px', fontSize: '13px',
          background: msg.tipo === 'ok' ? '#052e16' : '#450a0a',
          color: msg.tipo === 'ok' ? '#86efac' : '#fca5a5',
          border: `1px solid ${msg.tipo === 'ok' ? '#166534' : '#991b1b'}`,
        }}>
          {msg.texto}
        </div>
      )}

      <div style={{ marginBottom: '14px', fontSize: '12px', color: 'var(--text3)' }}>
        Importa el feed XML de Google Shopping de tu tienda (modo <strong>espejo</strong>: reemplaza el catálogo completo).
        Compatible con WooCommerce, Shopify y PrestaShop.
      </div>

      {/* Buscador */}
      <form onSubmit={handleBuscar} style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        <input
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
          placeholder="Buscar por nombre..."
          style={{ flex: 1, padding: '7px 12px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text1)', fontSize: '13px' }}
        />
        <button type="submit" style={{ padding: '7px 14px', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: '6px', cursor: 'pointer', color: 'var(--text1)', fontSize: '13px' }}>
          Buscar
        </button>
      </form>

      {/* Tabla */}
      {loading ? (
        <div style={{ color: 'var(--text3)', padding: '20px' }}>Cargando...</div>
      ) : lista.length === 0 ? (
        <div style={{ color: 'var(--text3)', padding: '20px', textAlign: 'center' }}>
          Sin productos. Importa un XML de Google Shopping para empezar.
        </div>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)', color: 'var(--text3)', textAlign: 'left' }}>
                  <th style={{ padding: '8px 10px' }}>Imagen</th>
                  <th style={{ padding: '8px 10px' }}>Producto</th>
                  <th style={{ padding: '8px 10px' }}>Categoría</th>
                  <th style={{ padding: '8px 10px' }}>Precio</th>
                  <th style={{ padding: '8px 10px' }}>Stock</th>
                  <th style={{ padding: '8px 10px' }}></th>
                </tr>
              </thead>
              <tbody>
                {lista.map(p => (
                  <tr key={p.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '8px 10px' }}>
                      {p.imagen_url
                        ? <img src={p.imagen_url} alt={p.titulo} style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }} onError={e => { e.target.style.display = 'none'; }} />
                        : <div style={{ width: '40px', height: '40px', background: 'var(--bg3)', borderRadius: '4px' }} />
                      }
                    </td>
                    <td style={{ padding: '8px 10px', maxWidth: '280px' }}>
                      <div style={{ fontWeight: '500', color: 'var(--text1)', marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.url_producto
                          ? <a href={p.url_producto} target="_blank" rel="noreferrer" style={{ color: 'var(--text1)', textDecoration: 'none' }}>{p.titulo}</a>
                          : p.titulo
                        }
                      </div>
                      {p.marca && <div style={{ fontSize: '11px', color: 'var(--text3)' }}>{p.marca}</div>}
                    </td>
                    <td style={{ padding: '8px 10px', color: 'var(--text2)', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.categoria || '—'}
                    </td>
                    <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                      {p.precio_rebajado && p.precio_rebajado < p.precio ? (
                        <>
                          <span style={{ textDecoration: 'line-through', color: 'var(--text3)', marginRight: '6px' }}>{formatPrecio(p.precio)}</span>
                          <span style={{ color: '#86efac', fontWeight: '600' }}>{formatPrecio(p.precio_rebajado)}</span>
                        </>
                      ) : (
                        <span style={{ fontWeight: '500' }}>{formatPrecio(p.precio)}</span>
                      )}
                    </td>
                    <td style={{ padding: '8px 10px' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: '10px', fontSize: '11px',
                        background: p.disponibilidad === 'in stock' ? '#052e16' : '#450a0a',
                        color: p.disponibilidad === 'in stock' ? '#86efac' : '#fca5a5',
                      }}>
                        {p.disponibilidad === 'in stock' ? 'Disponible' : p.disponibilidad}
                      </span>
                    </td>
                    <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                      <button
                        onClick={() => handleBorrar(p.id)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', fontSize: '14px', padding: '2px 6px' }}
                        title="Eliminar"
                      >🗑</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          {totalPags > 1 && (
            <div style={{ display: 'flex', gap: '8px', marginTop: '16px', justifyContent: 'center' }}>
              {page > 1 && (
                <button onClick={() => { setPage(p => p - 1); cargar(page - 1, busqueda); }}
                  style={{ padding: '5px 12px', borderRadius: '5px', border: '1px solid var(--border)', background: 'var(--bg2)', cursor: 'pointer', color: 'var(--text1)', fontSize: '12px' }}>
                  ← Anterior
                </button>
              )}
              <span style={{ padding: '5px 10px', fontSize: '12px', color: 'var(--text2)' }}>{page} / {totalPags}</span>
              {page < totalPags && (
                <button onClick={() => { setPage(p => p + 1); cargar(page + 1, busqueda); }}
                  style={{ padding: '5px 12px', borderRadius: '5px', border: '1px solid var(--border)', background: 'var(--bg2)', cursor: 'pointer', color: 'var(--text1)', fontSize: '12px' }}>
                  Siguiente →
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
